import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://clvykdyagubzwcbnbhtw.supabase.co';

export interface ELDConnection {
  id: string;
  name: string;
  provider: 'PRO_LOGS' | 'EVA_ELD' | 'SAMSARA';
  client_id?: string;
  client_secret?: string;
  api_key?: string;
  api_url?: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Get all ELD connections
 */
export async function getAllELDConnections(): Promise<{
  success: boolean;
  connections?: ELDConnection[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('eld_connections')
      .select('*')
      .order('created_at', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      connections: data || [],
    };
  } catch (error: any) {
    console.error('Error fetching ELD connections:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch ELD connections',
    };
  }
}

/**
 * Get enabled ProLogs connections
 */
export async function getEnabledProLogsConnections(): Promise<{
  success: boolean;
  connections?: ELDConnection[];
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('eld_connections')
      .select('*')
      .eq('provider', 'PRO_LOGS')
      .eq('enabled', true)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      connections: data || [],
    };
  } catch (error: any) {
    console.error('Error fetching ProLogs connections:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch ProLogs connections',
    };
  }
}

/**
 * Create a new ELD connection
 */
export async function createELDConnection(connection: Omit<ELDConnection, 'id' | 'created_at' | 'updated_at'>): Promise<{
  success: boolean;
  connection?: ELDConnection;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('eld_connections')
      .insert(connection)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      connection: data,
    };
  } catch (error: any) {
    console.error('Error creating ELD connection:', error);
    return {
      success: false,
      error: error.message || 'Failed to create ELD connection',
    };
  }
}

/**
 * Update an ELD connection
 */
export async function updateELDConnection(id: string, updates: Partial<Omit<ELDConnection, 'id' | 'created_at' | 'updated_at'>>): Promise<{
  success: boolean;
  connection?: ELDConnection;
  error?: string;
}> {
  try {
    const { data, error } = await supabase
      .from('eld_connections')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      connection: data,
    };
  } catch (error: any) {
    console.error('Error updating ELD connection:', error);
    return {
      success: false,
      error: error.message || 'Failed to update ELD connection',
    };
  }
}

/**
 * Delete an ELD connection
 */
export async function deleteELDConnection(id: string): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('eld_connections')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error('Error deleting ELD connection:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete ELD connection',
    };
  }
}

/**
 * Test a ProLogs connection
 */
export async function testProLogsConnectionById(connectionId: string): Promise<{
  success: boolean;
  data?: any;
  error?: string;
}> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/prologs-proxy`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          endpoint: '/api/v1/trucks',
          method: 'GET',
          connection_id: connectionId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API request failed: ${response.status}`);
    }

    const data = await response.json();

    return {
      success: true,
      data,
    };
  } catch (error: any) {
    console.error('Error testing ProLogs connection:', error);
    return {
      success: false,
      error: error.message || 'Failed to test ProLogs connection',
    };
  }
}

/**
 * Get trucks from all enabled ProLogs connections
 */
export async function getTrucksFromAllProLogsConnections(): Promise<{
  success: boolean;
  trucks?: Array<any>;
  error?: string;
}> {
  try {
    // The prologs-proxy edge function reads the (locked) eld_connections table
    // server-side, fans out to every enabled ProLogs connection, and returns
    // the combined trucks tagged with _connection_id/_connection_name. The
    // browser never touches connection credentials.
    const { data, error } = await supabase.functions.invoke('prologs-proxy', {
      body: {
        endpoint: '/api/v1/trucks',
        method: 'GET',
        all_connections: true,
      },
    });

    if (error) throw new Error(error.message || 'Failed to fetch trucks');
    if (data?.error) throw new Error(data.error);

    return {
      success: true,
      trucks: Array.isArray(data) ? data : [],
    };
  } catch (error: any) {
    console.error('Error fetching trucks from all connections:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch trucks',
    };
  }
}
